import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, FileDown, Plus, Save, Trash2 } from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Table } from '@/components/ui/table'
import { Combobox } from '@/components/Combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/cn'
import { formatCurrencyPrecise, formatDate, formatDateTime } from '@/lib/locale'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { useReportBrand } from '@/lib/pdf'
import { useCoa } from '@/features/coa/queries'
import {
  useAccountMappings,
  useApproveSnapshot,
  useCFMappings,
  useClosePeriod,
  useDeleteCFMapping,
  useGenerateMonthlySnapshots,
  useGeneratePeriods,
  usePeriods,
  usePutCFMappings,
  useReopenPeriod,
  useSnapshot,
  useSnapshots,
  useUpdateAccountMapping,
} from '@/features/reporting/queries'
import type {
  CFAdjustmentType,
  CFMapping,
  CFSection,
  ReportingPeriod,
  ReportSnapshotDetail,
} from '@/features/reporting/types'

const CF_SECTIONS: CFSection[] = ['operating', 'investing', 'financing']
const CF_ADJUSTMENT_TYPES: CFAdjustmentType[] = ['balance_change', 'add_back', 'deduct']

function fmtCents(n: unknown): string {
  const v = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(v)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(v)
}

// ─── Cash Flow Mappings tab ───────────────────────────────────────────────

function CFMappingsTab() {
  const { toast } = useToast()
  const { data, isLoading } = useCFMappings()
  const { data: taxonomy } = useCoa()
  const putMappings = usePutCFMappings()
  const deleteMapping = useDeleteCFMapping()

  const [local, setLocal] = useState<CFMapping[] | null>(null)
  useEffect(() => {
    if (data?.mappings) setLocal(data.mappings)
  }, [data?.mappings])

  const [addOpen, setAddOpen] = useState(false)
  const [newCode, setNewCode] = useState<string | null>(null)
  const [newSection, setNewSection] = useState<CFSection>('operating')
  const [newCategory, setNewCategory] = useState('')
  const [newLineItem, setNewLineItem] = useState('')
  const [newAdj, setNewAdj] = useState<CFAdjustmentType>('balance_change')

  const [deleteOpen, setDeleteOpen] = useState<string | null>(null)

  const taxLookup = useMemo(() => {
    const map: Record<string, string> = {}
    for (const t of taxonomy ?? []) map[t.account_code] = t.subcategory_name
    return map
  }, [taxonomy])

  const taxOptions = useMemo(
    () =>
      (taxonomy ?? []).map((t) => ({
        value: t.account_code,
        label: t.account_code,
        description: `${t.subcategory_name} (${t.statement_type} > ${t.main_category})`,
      })),
    [taxonomy],
  )

  async function handleSaveAll() {
    if (!local) return
    try {
      await putMappings.mutateAsync(local)
      toast({ title: `${local.length} cash flow mapping(s) saved.` })
    } catch (err) {
      toast({
        title: 'Save Failed',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  async function handleAdd() {
    if (!newCode) {
      toast({ title: 'Please select an account code.', variant: 'destructive' })
      return
    }
    if (!newLineItem.trim()) {
      toast({ title: 'Please enter a line item name.', variant: 'destructive' })
      return
    }
    if (local?.some((m) => m.account_code === newCode)) {
      toast({
        title: `Account ${newCode} is already mapped.`,
        variant: 'destructive',
      })
      return
    }
    const row: CFMapping = {
      account_code: newCode,
      cf_section: newSection,
      cf_category: newCategory.trim(),
      cf_line_item: newLineItem.trim(),
      adjustment_type: newAdj,
      display_order: 0,
    }
    try {
      await putMappings.mutateAsync([row])
      toast({ title: `Mapping added for account ${newCode}.` })
      setAddOpen(false)
      setNewCode(null)
      setNewCategory('')
      setNewLineItem('')
    } catch (err) {
      toast({
        title: 'Failed to Add Mapping',
        description: err instanceof Error ? err.message : 'Could not save the mapping.',
        variant: 'destructive',
      })
    }
  }

  async function handleDelete(code: string) {
    try {
      await deleteMapping.mutateAsync(code)
      toast({ title: `Mapping for ${code} removed.` })
      setDeleteOpen(null)
    } catch (err) {
      toast({
        title: 'Remove Failed',
        description: err instanceof Error ? err.message : 'Could not remove the mapping.',
        variant: 'destructive',
      })
    }
  }

  function updateRow(code: string, patch: Partial<CFMapping>) {
    setLocal((prev) =>
      (prev ?? []).map((m) => (m.account_code === code ? { ...m, ...patch } : m)),
    )
  }

  const rows = local ?? []
  // The Save Changes button writes the entire `local` array back. Treat
  // the snapshot as dirty whenever the in-memory rows diverge from the
  // server payload that hydrated them - typing in any inline cell editor
  // (section/category/line item/adjustment/order) flips this on.
  const dirty = useDirty(local, data?.mappings ?? null)

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Configure how accounts map to Cash Flow Statement sections.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={() => setAddOpen(true)}>
          <Plus /> Add Mapping
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleSaveAll} disabled={!rows.length || !dirty || putMappings.isPending}>
          <Save /> Save Changes
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <EmptyState title="No cash flow mappings" description='Click "Add Mapping" to create one.' />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table className="w-full min-w-[900px] text-sm">
            <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Account Name</th>
                <th className="px-3 py-2 text-left">CF Section</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Line Item</th>
                <th className="px-3 py-2 text-left">Adjustment</th>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.account_code} className="border-t">
                  <td className="px-3 py-2 font-semibold">{row.account_code}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {taxLookup[row.account_code] ?? ''}
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={row.cf_section}
                      onValueChange={(v) => updateRow(row.account_code, { cf_section: v as CFSection })}
                    >
                      <SelectTrigger className="h-8 w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CF_SECTIONS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={row.cf_category}
                      onChange={(e) => updateRow(row.account_code, { cf_category: e.currentTarget.value })}
                      placeholder="Category"
                      className="h-8 w-[160px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={row.cf_line_item}
                      onChange={(e) => updateRow(row.account_code, { cf_line_item: e.currentTarget.value })}
                      placeholder="Line Item"
                      className="h-8 w-[180px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={row.adjustment_type}
                      onValueChange={(v) =>
                        updateRow(row.account_code, { adjustment_type: v as CFAdjustmentType })
                      }
                    >
                      <SelectTrigger className="h-8 w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CF_ADJUSTMENT_TYPES.map((a) => (
                          <SelectItem key={a} value={a}>
                            {a.replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number"
                      value={row.display_order}
                      onChange={(e) =>
                        updateRow(row.account_code, { display_order: parseInt(e.currentTarget.value) || 0 })
                      }
                      className="h-8 w-[80px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove"
                      onClick={() => setDeleteOpen(row.account_code)}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Cash Flow Mapping"
        description="Select an account and map it to a CF statement section."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAdd}>
              <Plus /> Add
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField
            id="add-acct"
            label="Account Code"
            control={
              <Combobox
                value={newCode}
                onChange={(code) => {
                  setNewCode(code as string | null)
                  if (code && !newLineItem) {
                    const tax = (taxonomy ?? []).find((t) => t.account_code === code)
                    if (tax) setNewLineItem(tax.subcategory_name)
                  }
                }}
                options={taxOptions}
                placeholder="Search account…"
              />
            }
          />
          <FormField
            id="add-sec"
            label="CF Section"
            control={
              <Select value={newSection} onValueChange={(v) => setNewSection(v as CFSection)}>
                <SelectTrigger id="add-sec">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CF_SECTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <FormField
            id="add-cat"
            label="Category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.currentTarget.value)}
            placeholder="e.g. Working Capital"
          />
          <FormField
            id="add-line"
            label="Line Item"
            value={newLineItem}
            onChange={(e) => setNewLineItem(e.currentTarget.value)}
            placeholder="e.g. Inventory"
          />
          <FormField
            id="add-adj"
            label="Adjustment Type"
            control={
              <Select value={newAdj} onValueChange={(v) => setNewAdj(v as CFAdjustmentType)}>
                <SelectTrigger id="add-adj">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CF_ADJUSTMENT_TYPES.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
        </div>
      </Modal>

      <Modal.Confirm
        open={deleteOpen !== null}
        onOpenChange={(open) => !open && setDeleteOpen(null)}
        title="Remove Mapping"
        body={
          deleteOpen
            ? `Remove cash flow mapping for account ${deleteOpen}? This account will no longer appear in Cash Flow reports until re-mapped.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => {
          if (deleteOpen) void handleDelete(deleteOpen)
        }}
        busy={deleteMapping.isPending}
      />
    </div>
  )
}

// ─── Periods tab ─────────────────────────────────────────────────────────

function PeriodsTab() {
  const currentYear = new Date().getFullYear()
  const regional = useRegionalDefaults()
  const [year, setYear] = useState(currentYear)
  const [fiscalMonth, setFiscalMonth] = useState(() => String(regional.fiscalStartMonth))
  useEffect(() => {
    if (regional.isLoaded) setFiscalMonth((cur) => (cur === '1' ? String(regional.fiscalStartMonth) : cur))
  }, [regional.isLoaded, regional.fiscalStartMonth])
  const { data, isLoading, refetch } = usePeriods(year)
  const generate = useGeneratePeriods()
  const close = useClosePeriod()
  const reopen = useReopenPeriod()
  const { toast } = useToast()
  const [closeId, setCloseId] = useState<number | null>(null)
  const [reopenId, setReopenId] = useState<number | null>(null)

  async function handleGenerate() {
    try {
      await generate.mutateAsync({ year, fiscalStartMonth: parseInt(fiscalMonth) })
      toast({ title: `12 accounting periods generated for FY ${year}.` })
    } catch (err) {
      toast({
        title: 'Generation Failed',
        description: err instanceof Error ? err.message : 'May already exist.',
        variant: 'destructive',
      })
    }
  }

  async function handleClose() {
    if (closeId === null) return
    try {
      await close.mutateAsync(closeId)
      toast({ title: 'Period closed.' })
      setCloseId(null)
    } catch (err) {
      toast({
        title: 'Close Failed',
        description: err instanceof Error ? err.message : 'Could not close.',
        variant: 'destructive',
      })
    }
  }

  async function handleReopen() {
    if (reopenId === null) return
    try {
      await reopen.mutateAsync(reopenId)
      toast({ title: 'Period reopened.' })
      setReopenId(null)
    } catch (err) {
      toast({
        title: 'Reopen Failed',
        description: err instanceof Error ? err.message : 'Could not reopen.',
        variant: 'destructive',
      })
    }
  }

  const periods = data?.periods ?? []

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Manage fiscal year reporting periods. Generate periods for a fiscal year, then close them
        month by month as you finalize books.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FormField
          id="period-year"
          label="Fiscal Year"
          type="number"
          min={2020}
          max={2030}
          value={String(year)}
          onChange={(e) => setYear(parseInt(e.currentTarget.value) || currentYear)}
        />
        <FormField
          id="period-fm"
          label="Fiscal Start Month"
          control={
            <Select value={fiscalMonth} onValueChange={setFiscalMonth}>
              <SelectTrigger id="period-fm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  'January','February','March','April','May','June',
                  'July','August','September','October','November','December',
                ].map((name, i) => (
                  <SelectItem key={name} value={String(i + 1)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={() => refetch()}>
          Load Periods
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={generate.isPending}
        >
          Generate Periods
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : periods.length === 0 ? (
        <EmptyState
          title="No periods found"
          description={`Select fiscal year and start month, then click "Generate Periods".`}
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table className="w-full min-w-[640px] text-sm">
            <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-left">Key</th>
                <th className="px-3 py-2 text-left">Start</th>
                <th className="px-3 py-2 text-left">End</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p: ReportingPeriod) => (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2 font-semibold">Period {p.period_number}</td>
                  <td className="px-3 py-2">{p.period_key}</td>
                  <td className="px-3 py-2">{formatDate(p.start_date)}</td>
                  <td className="px-3 py-2">{formatDate(p.end_date)}</td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        p.status === 'open' ? 'success' : p.status === 'closed' ? 'warning' : 'secondary'
                      }
                    >
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {p.status === 'open' ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setCloseId(p.id)}>
                        Close
                      </Button>
                    ) : p.status === 'closed' ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setReopenId(p.id)}>
                        Reopen
                      </Button>
                    ) : (
                      <span className="text-xs italic text-muted-foreground">Locked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal.Confirm
        open={closeId !== null}
        onOpenChange={(open) => !open && setCloseId(null)}
        title="Close Period"
        body="Closing this period will prevent any new transactions from being posted to it. You can reopen the period later if needed."
        confirmLabel="Close"
        confirmVariant="primary"
        onConfirm={handleClose}
        busy={close.isPending}
      />

      <Modal.Confirm
        open={reopenId !== null}
        onOpenChange={(open) => !open && setReopenId(null)}
        title="Reopen Period"
        body="Reopening will allow new transactions to be posted. Only reopen if corrections are needed."
        confirmLabel="Reopen"
        confirmVariant="primary"
        onConfirm={handleReopen}
        busy={reopen.isPending}
      />
    </div>
  )
}

// ─── Account Mappings tab ───────────────────────────────────────────────

function AccountMappingsTab() {
  const { data, isLoading } = useAccountMappings()
  const { data: taxonomy } = useCoa()
  const update = useUpdateAccountMapping()
  const { toast } = useToast()
  const [editingId, setEditingId] = useState<number | null>(null)

  const taxOptions = useMemo(
    () =>
      (taxonomy ?? []).map((t) => ({
        value: t.account_code,
        label: t.account_code,
        description: `${t.subcategory_name} (${t.statement_type} > ${t.main_category})`,
      })),
    [taxonomy],
  )

  async function handleSave(id: number, code: string) {
    try {
      await update.mutateAsync({ id, mapped_account_code: code, mapping_status: 'confirmed' })
      toast({ title: 'Mapping updated.' })
      setEditingId(null)
    } catch (err) {
      toast({
        title: 'Update Failed',
        description: err instanceof Error ? err.message : 'Could not update.',
        variant: 'destructive',
      })
    }
  }

  const rows = data?.mappings ?? []

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Customer account mappings saved during Trial Balance imports. Click &quot;Mapped To&quot; to change it.
      </p>
      {isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No mappings yet"
          description="Import a Trial Balance to create them."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table className="w-full min-w-[640px] text-sm">
            <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
              <tr>
                <th className="px-3 py-2 text-left">External Code</th>
                <th className="px-3 py-2 text-left">External Name</th>
                <th className="px-3 py-2 text-left">Mapped To</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-2 font-semibold">{m.external_account_code}</td>
                  <td className="px-3 py-2">{m.external_account_name}</td>
                  <td className="px-3 py-2">
                    {editingId === m.id ? (
                      <Combobox
                        value={m.mapped_account_code}
                        onChange={(v) => v && handleSave(m.id, v as string)}
                        options={taxOptions}
                        placeholder="Search account…"
                      />
                    ) : m.mapped_account_code ? (
                      m.mapped_account_code
                    ) : (
                      <span className="italic text-destructive">Unmapped</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        m.mapping_status === 'confirmed' ? 'success' : 'warning'
                      }
                    >
                      {m.mapping_status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setEditingId((prev) => (prev === m.id ? null : m.id))
                      }
                    >
                      {editingId === m.id ? 'Cancel' : 'Edit'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ─── Snapshots tab ──────────────────────────────────────────────────────

type SnapshotSortCol = 'report_type' | 'period' | 'status' | 'created_at'

function SnapshotsTab() {
  const { data, isLoading } = useSnapshots()
  const [sortCol, setSortCol] = useState<SnapshotSortCol>('created_at')
  const [sortAsc, setSortAsc] = useState(false)
  const [viewId, setViewId] = useState<number | null>(null)
  const { toast } = useToast()
  const approve = useApproveSnapshot()
  const generate = useGenerateMonthlySnapshots()

  const sorted = useMemo(() => {
    const rows = [...(data?.snapshots ?? [])]
    rows.sort((a, b) => {
      let av: string, bv: string
      if (sortCol === 'period') {
        av = a.period_end || ''
        bv = b.period_end || ''
      } else if (sortCol === 'created_at') {
        av = a.created_at || ''
        bv = b.created_at || ''
      } else {
        av = String(a[sortCol] ?? '').toLowerCase()
        bv = String(b[sortCol] ?? '').toLowerCase()
      }
      if (av < bv) return sortAsc ? -1 : 1
      if (av > bv) return sortAsc ? 1 : -1
      return 0
    })
    return rows
  }, [data?.snapshots, sortCol, sortAsc])

  function toggleSort(col: SnapshotSortCol) {
    if (col === sortCol) setSortAsc((v) => !v)
    else {
      setSortCol(col)
      setSortAsc(true)
    }
  }

  async function handleApprove(id: number) {
    try {
      await approve.mutateAsync(id)
      toast({ title: 'Snapshot approved.' })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Approval failed.',
        variant: 'destructive',
      })
    }
  }

  async function handleGenerate() {
    try {
      await generate.mutateAsync()
      toast({ title: 'Monthly snapshots generated for the previous month.' })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Generation failed.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          <strong>Draft</strong> snapshots can still be edited/deleted. <strong>Approved</strong> snapshots are locked for audit trail.
        </p>
        <Button type="button" variant="primary" size="sm" onClick={handleGenerate} disabled={generate.isPending}>
          Generate Now
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : sorted.length === 0 ? (
        <EmptyState
          title="No report snapshots saved yet"
          description="Go to Reporting, generate any report, then click Save as Snapshot."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table className="w-full min-w-[720px] text-sm">
            <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
              <tr>
                {(
                  [
                    ['report_type', 'Report Type'],
                    ['period', 'Period'],
                    ['status', 'Status'],
                    ['created_at', 'Created'],
                  ] as Array<[SnapshotSortCol, string]>
                ).map(([col, label]) => (
                  <th
                    key={col}
                    className="cursor-pointer select-none px-3 py-2 text-left"
                    onClick={() => toggleSort(col)}
                  >
                    {label} {sortCol === col ? (sortAsc ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => {
                const period = s.period_start
                  ? `${formatDate(s.period_start)} – ${formatDate(s.period_end)}`
                  : `As of ${formatDate(s.period_end)}`
                return (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2">{s.report_type}</td>
                    <td className="px-3 py-2">{period}</td>
                    <td className="px-3 py-2">
                      <Badge variant={s.status === 'approved' ? 'success' : 'default'}>{s.status}</Badge>
                    </td>
                    <td className="px-3 py-2">{formatDateTime(s.created_at)}</td>
                    <td className="flex flex-wrap gap-2 px-3 py-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setViewId(s.id)}>
                        View
                      </Button>
                      {s.status === 'draft' ? (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => handleApprove(s.id)}
                          disabled={approve.isPending}
                        >
                          Approve
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      )}

      <SnapshotModal id={viewId} onClose={() => setViewId(null)} />
    </div>
  )
}

function SnapshotModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { data, isLoading, isError } = useSnapshot(id)
  const bodyRef = useRef<HTMLDivElement>(null)
  const brand = useReportBrand()

  const body = useMemo(() => {
    if (!data) return null
    const rd = typeof data.report_data === 'string' ? JSON.parse(data.report_data) : data.report_data
    return <SnapshotBody rd={rd as Record<string, unknown>} />
  }, [data])

  const titleText = data ? snapshotTitle(data) : 'Snapshot'
  const filenameBase = data ? snapshotFilename(data) : 'snapshot'

  const exportPdf = async () => {
    const mod = await import('@/features/reporting/export')
    mod.downloadPdfFromRoot(bodyRef.current, titleText, brand)
  }
  const exportCsv = async () => {
    const mod = await import('@/features/reporting/export')
    mod.downloadCsvFromRoot(bodyRef.current, `${filenameBase}.csv`, brand, titleText)
  }

  const canExport = !!data && !isLoading && !isError

  return (
    <Modal
      open={id !== null}
      onOpenChange={(open) => !open && onClose()}
      title={titleText}
      className="max-w-4xl"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={exportPdf} disabled={!canExport}>
            <FileDown className="size-4" /> Download PDF
          </Button>
          <Button type="button" variant="outline" onClick={exportCsv} disabled={!canExport}>
            <Download className="size-4" /> Download CSV
          </Button>
          <Button type="button" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load snapshot.</p>
      ) : (
        <div ref={bodyRef}>{body}</div>
      )}
    </Modal>
  )
}

function snapshotFilename(d: ReportSnapshotDetail): string {
  const range = d.period_start
    ? `${d.period_start}_to_${d.period_end}`
    : `asof_${d.period_end}`
  return `${d.report_type}_${range}`.replace(/\s+/g, '_')
}

function snapshotTitle(d: ReportSnapshotDetail): string {
  const range = d.period_start
    ? `${formatDate(d.period_start)} to ${formatDate(d.period_end)}`
    : `As of ${formatDate(d.period_end)}`
  return `${d.report_type} - ${range}`
}

// Render the snapshot payload - covers BS / IS / CF / TB shapes.
function SnapshotBody({ rd }: { rd: Record<string, unknown> }) {
  const rendered: React.ReactNode[] = []
  const r = rd as unknown as {
    balanceSheet?: unknown
    periods?: unknown[]
    incomeStatement?: unknown
    columns?: unknown[]
    operating?: unknown
    accounts?: unknown[]
  }
  const asBS = r.balanceSheet ?? r.periods?.[0]
  if (asBS && typeof asBS === 'object' && 'sections' in asBS) {
    rendered.push(<RenderBS key="bs" bs={asBS as BSLike} />)
  }
  const asIS = r.incomeStatement ?? r.columns?.[0]
  if (asIS && typeof asIS === 'object' && 'categories' in asIS) {
    rendered.push(<RenderIS key="is" is={asIS as ISLike} />)
  }
  if (r.operating) rendered.push(<RenderCF key="cf" rd={rd} />)
  if (Array.isArray(r.accounts)) rendered.push(<RenderTB key="tb" rd={rd} />)
  if (!rendered.length) {
    return (
      <pre className="max-h-[60vh] overflow-auto text-xs">{JSON.stringify(rd, null, 2)}</pre>
    )
  }
  return <div className="flex flex-col gap-4 text-sm">{rendered}</div>
}

interface BSLike {
  sections: Record<string, Record<string, Record<string, number | string>>>
  totals?: { totalAssets?: number | string; totalLiabilities?: number | string; totalEquity?: number | string }
}
interface ISLike {
  categories: Record<string, Record<string, number | string>>
  totals?: { totalRevenue?: number | string; totalExpenses?: number | string; netIncome?: number | string }
}

function RenderBS({ bs }: { bs: BSLike }) {
  return (
    <div>
      <h4 className="mb-2 font-semibold">Balance Sheet</h4>
      <Table className="w-full border-collapse text-sm">
        <thead>
          <tr className={cn('bg-muted font-semibold')}>
            <th className="px-2 py-1 text-left">Account</th>
            <th className="px-2 py-1 text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          {['Assets', 'Liabilities', 'Equity'].map((section) => {
            const block = bs.sections?.[section]
            if (!block) return null
            return (
              <>
                <tr key={section + '-hdr'} className="bg-muted/60 font-bold">
                  <td colSpan={2} className="px-2 py-1">{section}</td>
                </tr>
                {Object.entries(block).flatMap(([cat, subs]) => [
                  <tr key={section + '-' + cat + '-hdr'} className="bg-muted/40 font-semibold">
                    <td colSpan={2} className="px-2 py-1 pl-4">{cat}</td>
                  </tr>,
                  ...Object.entries(subs).map(([sub, val]) => (
                    <tr key={section + '-' + cat + '-' + sub} className="border-t">
                      <td className="px-2 py-1 pl-8">{sub}</td>
                      <td className="px-2 py-1 text-right">{fmtCents(val)}</td>
                    </tr>
                  )),
                ])}
              </>
            )
          })}
          {bs.totals ? (
            <>
              <tr className="bg-muted font-bold">
                <td className="px-2 py-1">Total Assets</td>
                <td className="px-2 py-1 text-right">{fmtCents(bs.totals.totalAssets)}</td>
              </tr>
              <tr className="bg-muted font-bold">
                <td className="px-2 py-1">Total Liabilities</td>
                <td className="px-2 py-1 text-right">{fmtCents(bs.totals.totalLiabilities)}</td>
              </tr>
              <tr className="bg-muted font-bold">
                <td className="px-2 py-1">Total Equity</td>
                <td className="px-2 py-1 text-right">{fmtCents(bs.totals.totalEquity)}</td>
              </tr>
            </>
          ) : null}
        </tbody>
      </Table>
    </div>
  )
}

function RenderIS({ is }: { is: ISLike }) {
  return (
    <div>
      <h4 className="mb-2 font-semibold">Income Statement</h4>
      <Table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted font-semibold">
            <th className="px-2 py-1 text-left">Account</th>
            <th className="px-2 py-1 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(is.categories ?? {}).flatMap(([cat, subs]) => [
            <tr key={cat + '-hdr'} className="bg-muted/60 font-semibold">
              <td colSpan={2} className="px-2 py-1">{cat}</td>
            </tr>,
            ...Object.entries(subs).map(([sub, val]) => (
              <tr key={cat + '-' + sub} className="border-t">
                <td className="px-2 py-1 pl-6">{sub}</td>
                <td className="px-2 py-1 text-right">{fmtCents(val)}</td>
              </tr>
            )),
          ])}
          {is.totals ? (
            <>
              <tr className="bg-muted font-bold">
                <td className="px-2 py-1">Revenue</td>
                <td className="px-2 py-1 text-right">{fmtCents(is.totals.totalRevenue)}</td>
              </tr>
              <tr className="font-semibold">
                <td className="px-2 py-1">Expenses</td>
                <td className="px-2 py-1 text-right">{fmtCents(is.totals.totalExpenses)}</td>
              </tr>
              <tr className="bg-muted font-bold">
                <td className="px-2 py-1">Net Income</td>
                <td className="px-2 py-1 text-right">{fmtCents(is.totals.netIncome)}</td>
              </tr>
            </>
          ) : null}
        </tbody>
      </Table>
    </div>
  )
}

interface CFLike {
  operating?: {
    netIncome?: number | string
    adjustments?: Array<{ lineItem: string; amount: number | string }>
    total?: number | string
  }
  investing?: { items?: Array<{ lineItem: string; amount: number | string }>; total?: number | string }
  financing?: { items?: Array<{ lineItem: string; amount: number | string }>; total?: number | string }
  summary?: { endingCash?: number | string }
}

function RenderCF({ rd }: { rd: Record<string, unknown> }) {
  const cf = rd as unknown as CFLike
  return (
    <div>
      <h4 className="mb-2 font-semibold">Cash Flow</h4>
      <Table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted font-semibold">
            <th className="px-2 py-1 text-left">Item</th>
            <th className="px-2 py-1 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-muted/60 font-bold">
            <td colSpan={2} className="px-2 py-1">Operating</td>
          </tr>
          <tr className="border-t">
            <td className="px-2 py-1 pl-6">Net Income</td>
            <td className="px-2 py-1 text-right">{fmtCents(cf.operating?.netIncome)}</td>
          </tr>
          {cf.operating?.adjustments?.map((a, i) => (
            <tr key={i} className="border-t">
              <td className="px-2 py-1 pl-8">{a.lineItem}</td>
              <td className="px-2 py-1 text-right">{fmtCents(a.amount)}</td>
            </tr>
          ))}
          <tr className="bg-muted font-bold">
            <td className="px-2 py-1">Net Cash from Operating</td>
            <td className="px-2 py-1 text-right">{fmtCents(cf.operating?.total)}</td>
          </tr>
          <tr className="bg-muted/60 font-bold">
            <td colSpan={2} className="px-2 py-1">Investing</td>
          </tr>
          {cf.investing?.items?.map((a, i) => (
            <tr key={i} className="border-t">
              <td className="px-2 py-1 pl-6">{a.lineItem}</td>
              <td className="px-2 py-1 text-right">{fmtCents(a.amount)}</td>
            </tr>
          ))}
          <tr className="bg-muted font-bold">
            <td className="px-2 py-1">Net Cash from Investing</td>
            <td className="px-2 py-1 text-right">{fmtCents(cf.investing?.total)}</td>
          </tr>
          <tr className="bg-muted/60 font-bold">
            <td colSpan={2} className="px-2 py-1">Financing</td>
          </tr>
          {cf.financing?.items?.map((a, i) => (
            <tr key={i} className="border-t">
              <td className="px-2 py-1 pl-6">{a.lineItem}</td>
              <td className="px-2 py-1 text-right">{fmtCents(a.amount)}</td>
            </tr>
          ))}
          <tr className="bg-muted font-bold">
            <td className="px-2 py-1">Net Cash from Financing</td>
            <td className="px-2 py-1 text-right">{fmtCents(cf.financing?.total)}</td>
          </tr>
          {cf.summary ? (
            <tr className="border-t bg-muted font-bold">
              <td className="px-2 py-1">Ending Cash</td>
              <td className="px-2 py-1 text-right">{fmtCents(cf.summary.endingCash)}</td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  )
}

interface TBLike {
  accounts?: Array<{ accountCode: string; accountName: string; debit: number | string; credit: number | string }>
  totalDebits?: number | string
  totalCredits?: number | string
}

function RenderTB({ rd }: { rd: Record<string, unknown> }) {
  const tb = rd as unknown as TBLike
  return (
    <div>
      <h4 className="mb-2 font-semibold">Trial Balance</h4>
      <Table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted font-semibold">
            <th className="px-2 py-1 text-left">Code</th>
            <th className="px-2 py-1 text-left">Account</th>
            <th className="px-2 py-1 text-right">Debit</th>
            <th className="px-2 py-1 text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {tb.accounts?.map((a, i) => (
            <tr key={i} className="border-t">
              <td className="px-2 py-1">{a.accountCode}</td>
              <td className="px-2 py-1">{a.accountName}</td>
              <td className="px-2 py-1 text-right">
                {Number(a.debit) > 0 ? fmtCents(a.debit) : ''}
              </td>
              <td className="px-2 py-1 text-right">
                {Number(a.credit) > 0 ? fmtCents(a.credit) : ''}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 bg-muted font-bold">
            <td colSpan={2} className="px-2 py-1">TOTALS</td>
            <td className="px-2 py-1 text-right">{fmtCents(tb.totalDebits)}</td>
            <td className="px-2 py-1 text-right">{fmtCents(tb.totalCredits)}</td>
          </tr>
        </tbody>
      </Table>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────

export function ReportingConfigPage() {
  const [tab, setTab] = useState<'cf' | 'periods' | 'mappings' | 'snapshots'>('cf')
  return (
    <Page
      title="Reporting Configuration"
      description="Configure cash-flow mappings, reporting periods, external account mappings, and snapshots."
      width="wide"
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="cf">Cash Flow Mappings</TabsTrigger>
          <TabsTrigger value="periods">Reporting Periods</TabsTrigger>
          <TabsTrigger value="mappings">Account Mappings</TabsTrigger>
          <TabsTrigger value="snapshots">Report Snapshots</TabsTrigger>
        </TabsList>
        <TabsContent value="cf">
          <Card className="p-4 sm:p-5">
            <CFMappingsTab />
          </Card>
        </TabsContent>
        <TabsContent value="periods">
          <Card className="p-4 sm:p-5">
            <PeriodsTab />
          </Card>
        </TabsContent>
        <TabsContent value="mappings">
          <Card className="p-4 sm:p-5">
            <AccountMappingsTab />
          </Card>
        </TabsContent>
        <TabsContent value="snapshots">
          <Card className="p-4 sm:p-5">
            <SnapshotsTab />
          </Card>
        </TabsContent>
      </Tabs>
    </Page>
  )
}
